<template>
  <div>
    <b-message
      v-if="sortedCoupons.length"
      type="is-dark"
    >
      <apart class="is-paddingless">
        <p>Create a new coupon.</p>
        <div class="buttons is-marginless">
          <button
            :class="{ 'is-loading': deleting }"
            :disabled="exporting || deleting"
            class="button is-danger is-rounded is-outlined"
            @click="deleteUnused"
          >
            Delete unused
          </button>
          <button
            :disabled="exporting || deleting"
            class="button is-dark is-rounded is-outlined"
            @click="copyCodes"
          >
            Copy codes
          </button>
          <button
            :class="{ 'is-loading': exporting }"
            :disabled="exporting"
            class="button is-dark is-rounded is-outlined"
            @click="exportCsv"
          >
            Export CSV
          </button>
          <button
            class="button is-dark is-rounded is-outlined"
            @click="addCoupon"
          >
            Create coupon(s)
          </button>
        </div>
      </apart>
    </b-message>

    <loading v-if="loading" />

    <div v-else>
      <b-table
        ref="table"
        :data="sortedCoupons"
        :mobile-cards="true"
        :paginated="
          !!sortedCoupons.length &&
            sortedCoupons.length > table.perPage &&
            table.isPaginated
        "
        :per-page="table.perPage"
        :current-page.sync="table.currentPage"
        :pagination-simple="table.isPaginationSimple"
        :loading="loading"
        hoverable
        striped
        detail-key="_id"
        class="has-vertical-align-middle"
      >
        <template slot="empty">
          <no-results
            :cta="{ label: 'Add coupon' }"
            icon="ticket-alt"
            title="No coupons"
            message="This promotions doesn't have any coupons."
            @click="addCoupon"
          />
        </template>

        <template slot-scope="props">
          <b-table-column
            field="_id"
            label="Coupon code"
          >
            <a
              class="has-text-dark"
              @click="$bus.$emit('copyToClipboard', `${props.row._id}`)"
            >
              <u>{{ props.row._id }}</u>
            </a>
          </b-table-column>

          <b-table-column
            label="Uses"
            width="100"
          >
            {{ props.row.useCount }} / {{ props.row.useLimit }}
          </b-table-column>

          <b-table-column
            label="Expires"
            width="160"
          >
            <span v-if="props.row.dateExpires">
              {{
                $moment(props.row.dateExpires.toDate()).format("DD/MM/YY HH:mm")
              }}
            </span>
            <span v-else>--</span>
          </b-table-column>

          <b-table-column
            custom-key="edit"
            width="1"
          >
            <button
              class="button"
              width="100"
              @click="edit(props.row)"
            >
              Edit
            </button>
          </b-table-column>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  where,
  writeBatch
} from "@firebase/firestore";

const BATCH_SIZE = 400; // Firestore caps batched writes at 500
export default {
  name: "CouponsTable",
  props: {
    promotionId: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      exporting: false,
      deleting: false,
      coupons: {},
      table: {
        isPaginated: true,
        isPaginationSimple: true,
        currentPage: 1,
        perPage: 10
      }
    };
  },
  computed: {
    sortedCoupons() {
      return this.$_(this.coupons)
        .orderBy(["dateCreated"], ["desc"])
        .toArray()
        .value();
    }
  },
  created() {
    this.getCoupons();
  },
  methods: {
    edit(coupon) {
      const modal = this.$modal.open({
        component: () => import("@shared/promotions/_couponModal"),
        parent: this,
        width: 720,
        hasModalCard: true,
        canCancel: ["escape", "outside"],
        props: {
          promotionId: this.promotionId,
          couponId: coupon._id
        },
        events: {
          success: () => {
            modal.close();
            this.getCoupons();
          }
        }
      });
    },
    addCoupon() {
      const modal = this.$modal.open({
        component: () => import("@shared/promotions/_couponModal"),
        parent: this,
        width: 720,
        hasModalCard: true,
        canCancel: ["escape", "outside"],
        props: {
          promotionId: this.promotionId
        },
        events: {
          success: () => {
            modal.close();
            this.getCoupons();
          }
        }
      });
    },
    /* The table itself is paginated, so exports fetch the promotion's full
    coupon list fresh. */
    async getAllCoupons() {
      let ref = collection(this.$firestore, `coupons`);
      if (this.promotionId) {
        ref = query(
          ref,
          where(
            "promotionRef",
            "==",
            doc(this.$firestore, `promotions/${this.promotionId}`)
          )
        );
      }
      const snapshot = await getDocs(
        query(ref, orderBy("dateCreated", "desc"))
      );
      return snapshot.docs.map(coupon =>
        this.$_.merge({}, { _id: coupon.id }, coupon.data())
      );
    },
    async deleteUnused() {
      this.deleting = true;
      let unused;
      try {
        unused = (await this.getAllCoupons()).filter(
          coupon => !coupon.useCount
        );
      } catch (error) {
        this.deleting = false;
        return this.$toast.open({
          message: `Error fetching coupons`,
          type: "is-danger"
        });
      }
      this.deleting = false;
      if (!unused.length) {
        return this.$toast.open({ message: `No unused coupons to delete` });
      }
      this.$confirm.open({
        parent: this,
        props: {
          action: "Delete",
          message: `You are about to permanently delete ${
            unused.length
          } unused coupon${
            unused.length === 1 ? "" : "s"
          } from this promotion. Redeemed coupons will be kept.`
        },
        events: {
          confirmed: async () => {
            this.deleting = true;
            try {
              for (let i = 0; i < unused.length; i += BATCH_SIZE) {
                const batch = writeBatch(this.$firestore);
                unused.slice(i, i + BATCH_SIZE).forEach(coupon => {
                  batch.delete(doc(this.$firestore, `coupons`, coupon._id));
                });
                await batch.commit();
              }
              unused.forEach(coupon => this.$delete(this.coupons, coupon._id));
              this.$toast.open({
                message: `${unused.length} coupon${
                  unused.length === 1 ? "" : "s"
                } deleted`
              });
            } catch (error) {
              this.$toast.open({
                message: `Error deleting coupons`,
                type: "is-danger"
              });
            } finally {
              this.deleting = false;
            }
          }
        }
      });
    },
    async copyCodes() {
      this.exporting = true;
      try {
        const coupons = await this.getAllCoupons();
        this.$bus.$emit(
          "copyToClipboard",
          coupons.map(coupon => coupon._id).join("\n")
        );
      } catch (error) {
        this.$toast.open({
          message: `Error fetching coupons`,
          type: "is-danger"
        });
      } finally {
        this.exporting = false;
      }
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const coupons = await this.getAllCoupons();
        const cell = value =>
          `"${String(
            value === undefined || value === null ? "" : value
          ).replace(/"/g, `""`)}"`;
        const rows = [
          ["Code", "Use limit", "Use count", "Expires", "Created"],
          ...coupons.map(coupon => [
            coupon._id,
            coupon.useLimit,
            coupon.useCount,
            coupon.dateExpires
              ? this.$moment(coupon.dateExpires.toDate()).format(
                  "YYYY-MM-DD HH:mm"
                )
              : "",
            coupon.dateCreated
              ? this.$moment(coupon.dateCreated.toDate()).format(
                  "YYYY-MM-DD HH:mm"
                )
              : ""
          ])
        ];
        const csv = rows.map(row => row.map(cell).join(",")).join("\n");
        const url = URL.createObjectURL(
          new Blob([csv], { type: "text/csv;charset=utf-8;" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `coupons-${this.promotionId || "all"}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.open({
          message: `Error exporting coupons`,
          type: "is-danger"
        });
      } finally {
        this.exporting = false;
      }
    },
    /* Load the promotion's full coupon list – the b-table paginates
    client-side, and promotions are capped at a scale (~1000 coupons) that a
    single query handles comfortably. */
    getCoupons() {
      this.getAllCoupons()
        .then(coupons => {
          this.loading = false;
          const byId = {};
          coupons.forEach(coupon => {
            byId[coupon._id] = coupon;
          });
          this.coupons = byId;
        })
        .catch(error => {
          this.loading = false;
          console.error(error.message);
        });
    }
  }
};
</script>
